import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AdobeClickType, API, CLAIM_STATUS_TYPE } from '../../constants/constants';
import {
    GelScreenDetectorContext,
    GelRowLayout,
    GelContainerLite,
    GelParagraph,
    GelBoxLayout,
    GelButton,
    GelSpinner,
    GelHeading2,
    GelHeading6,
    GelList,
    GelListItem,
    GelIcon,
    GelCheckbox,
    GelLabel,
    GelCheckboxGroup,
    GelForm,
    GelFormField,
    useGelFormData
} from '@tal-gel/components';
import { getGelTokens } from '@tal-gel/theming';
import { UserContext } from '../../common/usercontext/user.context';
import axios from 'axios';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { SAVE_LEGAL_DISCLOSURE_QUERY } from '../../graphql/queries/graphql-mutations';
import SESSION_STORAGE_KEY from '../../constants/storage.constant';
import { AdobeAnalytics } from '../../common/analytics/adobe-analytics';
import { ACTIVATION_EMAIL } from '../../graphql/queries/graphql-mutations';
import { useGetYourDeclarationPageContent } from '../../common/api/graphQLDataFetch';
import styled from '@emotion/styled';
import ResponsiveContainer from '../../components/responsive.container';
import { HorizontalSeperator } from '../claimdetails/policy/dividers';


const YourDeclaration = () => {
    const { global: { themeColorBackgroundLight, themeColorIconSuccess, themeColorIconWarning,
        themeColorGrayT20, themeColorBackgroundWarning, themeColorBackgroundDefault, sizeNone,
        sizeBaseX2, sizeBaseX3, sizeBaseUnit, sizeBaseX15, sizeBaseX5, sizeBaseX6, sizeBaseX10, fontSizeBodyXLarge,
        sizeBaseX14, sizeBaseX16, sizeBaseX4, fontSizeBase, fontSizeBodyLarge } } = getGelTokens();

    const navigate = useNavigate();
    const { screen, isXsScreen, isLargerThanLgScreen } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;
    const { contextData, setUserContext } = useContext(UserContext);
    const [{ aid, rid, ssoid, tpid }] = useCookies(['aid', 'rid', 'ssoid', 'tpid']);
    const { data, isFetched } = useGetYourDeclarationPageContent()
    const [submittingLegalAndPrivacyDisclosure, setSubmittingLegalAndPrivacyDisclosure] = useState(false);
    const {
        formData, 
        onFormDataChange
      } = useGelFormData({
        declarationCheckbox: false,
        privacyCheckbox: false,
      });
    const yourDetails = contextData.yourDetails;

    const onlyInactiveClaims = useMemo(() => {
        return contextData.claims.every(
          c => c?.status?.description?.includes(CLAIM_STATUS_TYPE.RTS) ||
               c?.status?.description === CLAIM_STATUS_TYPE.CL
        );
      }, [contextData.claims]);
      
    if (!contextData.memberDetails?.displayLegalDisclosure || !yourDetails) {
        navigate("/error");
    }

    useEffect(() => {
        if (window.sessionStorage.getItem(SESSION_STORAGE_KEY.SEND_ACTIVATION_EMAIL)) {
            sendSuccessfulActivationEmail();
        }

        AdobeAnalytics.PushPageViewEvent(tpid)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sendSuccessfulActivationEmail = () => {
        let requestData = {
            userName: window.sessionStorage.getItem(SESSION_STORAGE_KEY.SEND_ACTIVATION_EMAIL)
        };
        axios.post(API.BASEURL, {
            query: ACTIVATION_EMAIL,
            variables: {
                memberActivationSuccessModel: requestData
            }
        },
            {
                headers: {
                    'x-aid': aid,
                    'x-rid': rid,
                    ssoid,
                    tpid
                }
            })
            .then(async (response) => {
                if (response) {
                    console.log("Activation email sent")
                }
                else {
                    console.log("Failed to send activation email")
                }
                window.sessionStorage.setItem(SESSION_STORAGE_KEY.SEND_ACTIVATION_EMAIL, "");
            })
            .catch(httpError => {
                console.log('Failed to send successful activation email', httpError);
            });
    }

    const onSubmit = () => {
        AdobeAnalytics.PushClickEvent(tpid, data?.buttonText!, AdobeClickType.Button)

        setSubmittingLegalAndPrivacyDisclosure(true);
        axios.post(API.BASEURL, {
            query: SAVE_LEGAL_DISCLOSURE_QUERY,
        },
            {
                headers: {
                    'x-aid': aid,
                    'x-rid': rid,
                    ssoid,
                    tpid,
                    'sid': window.sessionStorage.getItem(SESSION_STORAGE_KEY.SESSIONID_TOKEN)
                }
            })
            .then(async (response) => {
                setSubmittingLegalAndPrivacyDisclosure(false);
                if (response.data?.data?.acceptLegalDisclosureForMemberClaims === true) {
                    if (contextData.memberDetails) {
                        contextData.memberDetails.displayLegalDisclosure = false;
                        setUserContext(contextData);
                    }
                    navigate("/claims");
                }
            })
            .catch(httpError => {
                console.error('Failed to save legal disclosure', httpError);
                setSubmittingLegalAndPrivacyDisclosure(false);
            });
    };

    if (!isFetched)
        return (<GelSpinner medium overlay />)

    const PageHeader = styled.div(() => ({
        padding: isXsScreen(screen) ? `${sizeBaseX15}px ${sizeBaseX5}px` : `${sizeBaseX15}px ${sizeBaseX10}px`,
        backgroundColor: themeColorBackgroundLight
    }));

    const Card = styled.div(() => ({
        color: themeColorBackgroundDefault,
        border: "1px solid",
        borderColor: themeColorGrayT20,
        borderRadius: sizeBaseUnit * 2
    }));

    return (
        <GelContainerLite id="yourDeclaration" style={{ padding: sizeNone, overflow: "hidden" }}>

            <PageHeader >
                <ResponsiveContainer>
                    <GelRowLayout style={{ padding: isLargerThanLgScreen(screen) ? `${sizeNone}px ${sizeBaseX10}px` : `${sizeNone}px ${sizeBaseX5}px` }}>
                        <GelHeading2 style={{ padding: 0 }}>{data?.pageHeader} {yourDetails?.firstName}</GelHeading2>
                        <GelParagraph style={{ paddingTop: sizeBaseX3 }}>{data?.subTitle}</GelParagraph>
                    </GelRowLayout>
                </ResponsiveContainer>
            </PageHeader>

            <ResponsiveContainer>
                <GelForm disableOnSubmit onSubmit={onSubmit} >
                    <GelRowLayout style={{ padding: `${sizeBaseX5}px ${sizeBaseX10}px` }}>

                        <GelParagraph style={{
                            fontWeight: 400,
                            fontSize: fontSizeBodyXLarge,
                            paddingTop: sizeBaseX10
                        }}>{data?.declarationInfo}</GelParagraph>

                        <GelContainerLite style={{ padding: 0 }}>
                            <GelHeading6 >{data?.whatYouCanDoHeader}</GelHeading6>
                            <GelList check style={{ paddingTop: sizeBaseX3 }}>
                                {data?.whatYouCanDoList.map((req) => (
                                    <GelListItem key={crypto.randomUUID()}>{req}</GelListItem>
                                ))}
                            </GelList>
                        </GelContainerLite>

                        <GelContainerLite style={{ padding: 0 }}>
                            <Card style={{ borderRadius: sizeBaseUnit, borderColor: themeColorBackgroundWarning, display: "flex", flexDirection: "row", width: '100%' }}>

                                <GelContainerLite style={{ width: sizeBaseX14, backgroundColor: "#FFE9D3", borderRadius: "4px 0px 0px 4px", padding: sizeBaseX4 }}>
                                    <GelIcon
                                        name="AlertCircle"
                                        color={themeColorIconWarning}
                                        width={sizeBaseX6}
                                        height={sizeBaseX6}
                                    />
                                </GelContainerLite>
                                <GelContainerLite style={{ padding: sizeBaseX4 }}>
                                    <GelLabel> {data?.alertHeader} </GelLabel>
                                    <GelParagraph> {data?.alertContent}</GelParagraph>
                                </GelContainerLite>
                            </Card>
                        </GelContainerLite>

                        <GelContainerLite style={{ padding: sizeNone }}>
                            <GelHeading6>{data?.needHelpHeader}</GelHeading6>

                            {[data?.needHelpStatement1, data?.needHelpStatement2].map((statement, index) => (
                                <GelBoxLayout
                                    key={index}
                                    alignment="start"
                                    space={[0.5, 4]}
                                    gutter="none"
                                    style={{ paddingTop: sizeBaseX3 }}
                                >
                                    <GelIcon
                                        style={{ paddingRight: sizeBaseX2 }}
                                        name="QuestionCircle"
                                        color={themeColorIconSuccess}
                                        width={sizeBaseX5}
                                        height={sizeBaseX5}
                                    />
                                    <GelParagraph style={{ fontSize: fontSizeBase, fontWeight: 500 }}>
                                        {statement}
                                    </GelParagraph>
                                </GelBoxLayout>
                            ))}

                        </GelContainerLite>

                        <HorizontalSeperator />

                        <GelContainerLite style={{ padding: sizeNone }}>
                            <GelHeading6 >{data?.beforeYouContinueHeader}</GelHeading6>
                            <GelParagraph style={{ paddingTop: sizeBaseX3, fontSize: fontSizeBodyLarge, fontWeight: 400 }}>
                                {data?.beforeYouContinueContent}
                            </GelParagraph>
                        </GelContainerLite>
                            
                        {!onlyInactiveClaims &&
                            <Card>
                                <GelContainerLite style={{ padding: "24px 16px" }}>
                                    <GelHeading6> {data?.legalDisclosure?.HeaderText} </GelHeading6>

                                    <GelRowLayout style={{ padding: "20px 0px" }}>
                                        <GelParagraph style={{
                                            fontWeight: 400,
                                            fontSize: fontSizeBodyLarge
                                        }}>
                                            <p dangerouslySetInnerHTML={{ __html: data?.legalDisclosure?.DeclarationText! }}></p>
                                        </GelParagraph>
                                    </GelRowLayout>

                                    <GelFormField label="">
                                        <Card
                                            style={{
                                                borderRadius: sizeBaseUnit,
                                                padding: "8px 16px"
                                            }}
                                        >
                                            <GelCheckboxGroup
                                                name=""
                                                required
                                                min={1}
                                                errorMsg={{
                                                    required: data?.legalDisclosure?.ValidationText
                                                }}
                                            >
                                                <GelCheckbox
                                                    name="declarationCheckbox" 
                                                    disabled={submittingLegalAndPrivacyDisclosure}
                                                    checked={formData.declarationCheckbox}
                                                    onChange={onFormDataChange} >
                                                    {data?.legalDisclosure?.AcceptButtonText}
                                                </GelCheckbox>
                                            </GelCheckboxGroup>
                                        </Card>
                                    </GelFormField>
                                </GelContainerLite>
                            </Card>
                        }

                        <Card>
                            <GelContainerLite style={{ padding: "24px 16px" }}>
                                <GelHeading6> {data?.privacyDeclaration?.HeaderText} </GelHeading6>

                                <GelRowLayout style={{ padding: "20px 0px" }}>
                                    <GelParagraph style={{
                                        fontWeight: 400,
                                        fontSize: fontSizeBodyLarge
                                    }}>
                                        <p dangerouslySetInnerHTML={{ __html: data?.privacyDeclaration?.DeclarationText! }}></p>
                                    </GelParagraph>
                                </GelRowLayout>

                                <GelFormField label="">
                                    <Card
                                        style={{
                                            borderRadius: sizeBaseUnit,
                                            padding: "8px 16px"
                                        }}
                                    >
                                        <GelCheckboxGroup
                                            name=""
                                            required
                                            min={1}
                                            errorMsg={{
                                                required: data?.privacyDeclaration?.ValidationText
                                            }}
                                        >
                                            <GelCheckbox
                                                name="privacyCheckbox"
                                                checked={formData.privacyCheckbox}
                                                disabled={submittingLegalAndPrivacyDisclosure}
                                                onChange={onFormDataChange} >
                                                {data?.privacyDeclaration?.AcceptButtonText}
                                            </GelCheckbox>
                                        </GelCheckboxGroup>
                                    </Card>
                                </GelFormField>
                            </GelContainerLite>
                        </Card>
                        <GelFormField style={{ paddingBottom: sizeBaseX16 }}>
                            <GelButton
                                name="" // Makes it a managed component
                                style={{ width: "100%" }}
                                primary large submit
                                disabled={submittingLegalAndPrivacyDisclosure}
                                loading={submittingLegalAndPrivacyDisclosure}
                            >
                                {data?.buttonText}
                            </GelButton>
                            { submittingLegalAndPrivacyDisclosure &&
                                <GelParagraph style={{paddingTop: '10px'}}>Please wait while we load your claim, this may take a few seconds...</GelParagraph>
                            }
                        </GelFormField>

                    </GelRowLayout>
                </GelForm>
            </ResponsiveContainer>

        </GelContainerLite>

    )
};

export default YourDeclaration  